import { isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { ColumnWidths, Row } from './types';

// const debug = Debug("guni/Utils");
const FILE_PARTS_SEPARATOR = '|';

export const deduceNameFromEmail = (emailAddress: string) => {
  const parts1 = emailAddress.split('@');
  const parts2 = parts1[0].split('+');
  const parts3 = parts2[0].split('.');
  return parts3.map((part) => part.substring(0, 1).toUpperCase() + part.substring(1).toLowerCase()).join(' ');
};

export const deepEqualsByTopLevelProperties = (obj1: any, obj2: any): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return out;
  }
  Object.keys(obj1)
    .concat(Object.keys(obj2))
    .forEach((key) => {
      out[key] = isEqual(obj1[key], obj2[key]);
    });
  return out;
};

export const deepFreeze = (arg: any, keyChain: any) => {
  if (Array.isArray(arg)) {
    Object.freeze(arg);
    arg.forEach((el) => deepFreeze(el, keyChain));
  } else if (typeof arg === 'object') {
    Object.freeze(arg);
    Object.keys(arg).forEach((key) => {
      if (keyChain === '*' || keyChain[key]) {
        deepFreeze(arg[key], keyChain === '*' ? '*' : keyChain[key]);
      }
    });
  }
};

export const deepFreezeEntities = (arg: any) => {
  deepFreeze(arg, {
    primaryKeyFields: '*',
    security: '*',
    defaultSort: '*',
    defaultFilters: '*',
    autoCompleterSearchExpression: '*',
    fields: {
      options: '*',
    },
  });
};

export const filePartsCompose = (title: string, mimetype: string, content: string) => {
  if (!title && !mimetype && !content) return null;
  if (!title || !mimetype || !content) throw new Error(`please supply all 3 file parts or none`);
  return [title, mimetype, content].join(FILE_PARTS_SEPARATOR);
};

export const filePartsGet = (combined: string) => {
  if (!combined) {
    return {
      title: null,
      mimetype: null,
      content: null,
    };
  }
  const parts = combined.split(FILE_PARTS_SEPARATOR);
  if (parts.length !== 3) {
    throw new Error(`invalid file property, had ${parts.length} parts - should be 3`);
  }
  return {
    title: parts[0],
    mimetype: parts[1],
    content: parts[2],
  };
};

export const filePartsMerge = (row: Row, fieldId: string) => {
  const mutableRow = row as Record<string, any>;
  mutableRow[fieldId] = filePartsCompose(
    mutableRow[`${fieldId}_title`],
    mutableRow[`${fieldId}_mimetype`],
    mutableRow[`${fieldId}_content`],
  );
  delete mutableRow[`${fieldId}_title`];
  delete mutableRow[`${fieldId}_mimetype`];
  delete mutableRow[`${fieldId}_content`];
};

export const filePartsSplit = <T extends Row>(row: T, fieldId: string) => {
  const mutableRow = row as Record<string, any>;
  if (!mutableRow[fieldId]) {
    // file field is blank
    return;
  }
  const { title, mimetype, content } = filePartsGet(String(mutableRow[fieldId]));
  mutableRow[`${fieldId}_title`] = title;
  mutableRow[`${fieldId}_mimetype`] = mimetype;
  mutableRow[`${fieldId}_content`] = content;
  delete mutableRow[fieldId];
};

export const getListRowLink = (listRowLink: string | ((key: string) => string) | null | undefined, key: string) => {
  if (!listRowLink) {
    throw new Error(`no listRowLink supplied`);
  }
  if (typeof listRowLink === 'function') {
    return listRowLink(key);
  }
  return listRowLink.replace('{key}', encodeURIComponent(key));
};

export const getSectionColumnWidths = (columnWidths?: ColumnWidths): ColumnWidths => {
  if (columnWidths) {
    return columnWidths;
  }
  return {
    xs: true,
    sm: true,
    md: true,
    lg: true,
    xl: true,
    xxl: true,
  };
};

export const getUuid = uuidv4;

export const imageBase64PartsCompose = (mimetype: string, content: string) =>
  !!mimetype && !!content ? `data:${mimetype};base64, ${content}` : null;

export const imageBase64PartsGet = (combined: string) => {
  const regex = new RegExp('^data:(.+?);base64,\\s*(.+)$');
  const parts = regex.exec(combined);
  console.log(combined, parts);
  if (parts && parts.length > 2) {
    return {
      mimetype: parts[1],
      content: parts[2],
    };
  }
  return null;
};

export const isBlank = (arg: any) => arg === undefined || arg === null || arg === '';

export const isUuid = (val: string) => !!/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.exec(val);

export const padNumber = (num: number, toWidth: number) => {
  let out = num.toFixed(0);
  if (out.length < toWidth) out = '0'.repeat(toWidth - out.length) + out;
  return out;
};
export const slugify = (arg: string): string =>
  arg
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^\w\s-]/g, '') // Remove non-word characters except spaces and hyphens
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with a single hyphen

export const stringToSentenceCase = (sentence: string) =>
  sentence.charAt(0).toUpperCase() + sentence.slice(1).toLowerCase();

export const stripAwsRegionFromUserId = (userId: string) => {
  const parts = userId.split(':');
  return parts.length > 1 ? parts[1] : userId;
};

export const truncate = (str: string, maxLength: number, useEllipsis: boolean = true) => {
  if (useEllipsis) {
    maxLength -= 3;
  }
  if (!str || str.length <= maxLength) {
    return str;
  }
  return str.substring(0, maxLength) + (useEllipsis ? '...' : '');
};
